import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { navIcons, LayoutGrid, LogOut, type Icon } from '@hpanel/web-common/components/icons'
import { PluginMenuItems } from '@hpanel/web-common/plugins'
import { useAuth } from '@hpanel/web-common/contexts/AuthContext'

/**
 * Navigation sidebar component for the client (tenant) portal.
 *
 * Mirrors the admin Sidebar's modern look-and-feel: lucide-react
 * icons, subtle tinted active state, dedicated "Account" section,
 * status footer.
 *
 * Display names map to icons via `navIcons`; the slug used for the
 * `data-testid="nav-link-<slug>"` attribute is derived from the name,
 * matching the algorithm used by the admin sidebar so existing
 * Playwright tests stay green.
 */

interface NavItem {
  name: string
  path: string
}

interface NavSection {
  title: string
  items: NavItem[]
}

// WordPress, DNS, PHP and Runtime used to have no entry here — they were only
// reachable from the admin console's sidebar or a domain's Manage panel.
const navSections: NavSection[] = [
  {
    title: 'Workspace',
    items: [
      { name: 'Dashboard', path: '/client' },
      { name: 'Container', path: '/client/container' },
      { name: 'Sites', path: '/client/sites' },
      { name: 'Domains', path: '/client/domains' },
      { name: 'Databases', path: '/client/databases' },
      { name: 'Files', path: '/client/files' },
      { name: 'SFTP', path: '/client/sftp' },
      { name: 'Email', path: '/client/email' },
      { name: 'WordPress', path: '/client/wordpress' },
      { name: 'DNS', path: '/client/dns' },
      { name: 'PHP', path: '/client/php' },
      { name: 'Runtime', path: '/client/runtime' },
      { name: 'Backups', path: '/client/backups' },
    ],
  },
  {
    title: 'Account',
    items: [
      { name: 'Account', path: '/client/account' },
      { name: 'Privacy', path: '/client/account/privacy' },
    ],
  },
]

const slugFor = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]+/g, '-')

export const ClientSidebar: React.FC = () => {
  const location = useLocation()
  const { user, logout } = useAuth()
  const [logoutError, setLogoutError] = React.useState<string | null>(null)
  const [loggingOut, setLoggingOut] = React.useState(false)
  const isAdmin = user?.role === 'admin'

  return (
    <aside
      data-testid="sidebar"
      className="w-64 bg-gray-50 dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 min-h-screen flex flex-col"
    >
      <div className="px-5 pt-5 pb-4 flex items-center gap-2.5">
        <div className="h-7 w-7 rounded-md bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 grid place-items-center text-sm font-medium">
          h
        </div>
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            hPanel
          </span>
          <span className="text-[11px] text-gray-500 dark:text-gray-400">
            Client portal
          </span>
        </div>
      </div>

      <nav className="flex-1 px-3 pb-4 overflow-y-auto space-y-4">
        {navSections.map((section) => (
        <div key={section.title}>
        <h3 className="px-2 mb-1.5 text-[11px] font-medium text-gray-500 dark:text-gray-500">
          {section.title}
        </h3>
        <ul className="space-y-0.5">
          {section.items.map((item) => {
            const isActive = location.pathname === item.path
            const IconCmp: Icon = navIcons[item.name] ?? LayoutGrid
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  data-testid={`nav-link-${slugFor(item.name)}`}
                  className={`
                    flex items-center gap-2.5 px-2 py-1.5 rounded-md
                    text-[13px] transition-colors
                    ${
                      isActive
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100'
                    }
                  `}
                >
                  <IconCmp
                    className="h-4 w-4 flex-shrink-0"
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                  <span>{item.name}</span>
                </Link>
              </li>
            )
          })}
        </ul>
        </div>
        ))}
      </nav>

      {/* Plugin-contributed menu items */}
      <PluginMenuItems
        surface="tenant"
        itemClassName="flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[13px] transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
      />

      {/*
        Sign out (bug #6). Same gap as the admin sidebar: `AuthContext.logout`
        existed and nothing called it, so a tenant had no way to end their
        session. Admin users viewing this plane get a link back to their own
        console alongside it.
      */}
      <div className="px-3 pb-2 space-y-0.5">
        {isAdmin && (
          <a
            href="/admin/dashboard"
            data-testid="nav-link-admin-console"
            className="flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[13px] transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <LayoutGrid className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
            <span>Admin console</span>
          </a>
        )}
        {logoutError && <p role="alert" className="text-sm text-red-600 mb-2">{logoutError}</p>}
        <button
          type="button"
          data-testid="logout-button"
          disabled={loggingOut}
          onClick={async () => {
            setLoggingOut(true)
            setLogoutError(null)
            try {
              await logout()
              window.location.assign('/login')
            } catch {
              setLogoutError('Sign out failed. Your session is still active. Please try again.')
            } finally {
              setLoggingOut(false)
            }
          }}
          className="w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md text-[13px] transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <LogOut className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} aria-hidden="true" />
          <span>Sign out</span>
        </button>
      </div>

      <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex flex-col gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
        {user && (
          <span data-testid="sidebar-username" className="truncate">
            Signed in as {user.username || user.email}
          </span>
        )}
        <span className="flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 rounded-full bg-green-500"
            aria-hidden="true"
          />
          <span>All systems normal</span>
        </span>
      </div>
    </aside>
  )
}
